// src/features/billing/AccountFormDialog.tsx
// "+ New account" — create dialog for a Chart-of-Accounts ledger account
// (CreateAccount). Built on the form-kit (FormDialog/TitleInput/AutoGrowTextarea)
// from src/components/form, same shape as src/features/localization/KeyCreateDialog.tsx.
//
// NOTE: CreateAccountRequest only carries entity/code/name/description/type/
// subtype/parent/currency/metadata — the previous generated scaffold also
// collected is_system/is_active/allow_manual_entries/current_balance/
// pending_balance, but those aren't accepted by the create RPC (a fresh
// ledger account always starts inactive/zero-balance server-side). This
// dialog only exposes fields the request actually has. `metadata` has no
// dedicated key/value editor in this codebase yet, so it's sent as `{}` —
// same simplification KeyCreateDialog makes for `tags: []`.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { useBilling } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { Account } from '@core/types/billing';
import { errMessage } from './format';

export interface AccountFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (account: Account) => void;
  onError: (message: string) => void;
}

const TYPE_OPTIONS = [
  { value: 'ACCOUNT_TYPE_UNSPECIFIED', label: 'Not set' },
  { value: 'ACCOUNT_TYPE_ASSET', label: 'Asset' },
  { value: 'ACCOUNT_TYPE_LIABILITY', label: 'Liability' },
  { value: 'ACCOUNT_TYPE_EQUITY', label: 'Equity' },
  { value: 'ACCOUNT_TYPE_REVENUE', label: 'Revenue' },
  { value: 'ACCOUNT_TYPE_EXPENSE', label: 'Expense' },
  { value: 'ACCOUNT_TYPE_CONTRA', label: 'Contra' },
];

const SUBTYPE_OPTIONS = [
  { value: 'ACCOUNT_SUBTYPE_UNSPECIFIED', label: 'Not set' },
  { value: 'ACCOUNT_SUBTYPE_CASH', label: 'Cash' },
  { value: 'ACCOUNT_SUBTYPE_ACCOUNTS_RECEIVABLE', label: 'Accounts receivable' },
  { value: 'ACCOUNT_SUBTYPE_PREPAID', label: 'Prepaid' },
  { value: 'ACCOUNT_SUBTYPE_ACCOUNTS_PAYABLE', label: 'Accounts payable' },
  { value: 'ACCOUNT_SUBTYPE_DEFERRED_REVENUE', label: 'Deferred revenue' },
  { value: 'ACCOUNT_SUBTYPE_TAX_PAYABLE', label: 'Tax payable' },
  { value: 'ACCOUNT_SUBTYPE_SUBSCRIPTION_REVENUE', label: 'Subscription revenue' },
  { value: 'ACCOUNT_SUBTYPE_USAGE_REVENUE', label: 'Usage revenue' },
  { value: 'ACCOUNT_SUBTYPE_SERVICE_REVENUE', label: 'Service revenue' },
  { value: 'ACCOUNT_SUBTYPE_PAYMENT_PROCESSING', label: 'Payment processing' },
  { value: 'ACCOUNT_SUBTYPE_REFUND', label: 'Refund' },
];

const CURRENCY_OPTIONS = [
  { value: 'CURRENCY_UNSPECIFIED', label: 'Not set' },
  { value: 'CURRENCY_USD', label: 'USD' },
  { value: 'CURRENCY_EUR', label: 'EUR' },
  { value: 'CURRENCY_RUB', label: 'RUB' },
  { value: 'CURRENCY_GBP', label: 'GBP' },
  { value: 'CURRENCY_JPY', label: 'JPY' },
  { value: 'CURRENCY_CNY', label: 'CNY' },
  { value: 'CURRENCY_KZT', label: 'KZT' },
  { value: 'CURRENCY_BYN', label: 'BYN' },
  { value: 'CURRENCY_AED', label: 'AED' },
  { value: 'CURRENCY_TRY', label: 'TRY' },
];

export function AccountFormDialog({ open, onClose, onCreated, onError }: AccountFormDialogProps) {
  const { t } = useI18n();
  const store = useBilling();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('ACCOUNT_TYPE_UNSPECIFIED');
  const [subtype, setSubtype] = useState('ACCOUNT_SUBTYPE_UNSPECIFIED');
  const [currency, setCurrency] = useState('CURRENCY_UNSPECIFIED');
  const [parentId, setParentId] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dirty = !!(code.trim() || name.trim() || description.trim() || parentId.trim());

  const reset = () => {
    setCode('');
    setName('');
    setDescription('');
    setType('ACCOUNT_TYPE_UNSPECIFIED');
    setSubtype('ACCOUNT_SUBTYPE_UNSPECIFIED');
    setCurrency('CURRENCY_UNSPECIFIED');
    setParentId('');
    setMoreOpen(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!code.trim() || !name.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const account = await store.CreateAccount({
        entity: {} as any,
        code: code.trim(),
        name: name.trim(),
        description: description.trim(),
        type: type !== 'ACCOUNT_TYPE_UNSPECIFIED' ? (type as any) : undefined,
        subtype: subtype !== 'ACCOUNT_SUBTYPE_UNSPECIFIED' ? (subtype as any) : undefined,
        parent: parentId.trim() ? ({ id: parentId.trim() } as any) : undefined,
        currency: currency !== 'CURRENCY_UNSPECIFIED' ? (currency as any) : undefined,
        metadata: {},
      });
      reset();
      onCreated(account);
    } catch (err) {
      onError(errMessage(err, t('billing.chartOfAccounts.saveError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('billing.chartOfAccounts.createSubmit')}
      canSubmit={!!code.trim() && !!name.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('billing.chartOfAccounts.createTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreOpen ? t('billing.chartOfAccounts.fewerFields') : t('billing.chartOfAccounts.moreFields')}
        </button>
      }
    >
      <div className="space-y-2 px-3.5 pb-2 pt-2">
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('billing.chartOfAccounts.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('account-create-code')?.focus()}
          aria-label={t('billing.chartOfAccounts.namePlaceholder')}
        />
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs text-muted-foreground">{t('billing.chartOfAccounts.code')}</span>
          <input
            id="account-create-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('billing.chartOfAccounts.codePlaceholder')}
            autoCapitalize="off"
            className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 font-mono text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>
        <AutoGrowTextarea
          value={description}
          onChange={setDescription}
          placeholder={t('billing.chartOfAccounts.descriptionPlaceholder')}
          minRows={2}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3 pt-1">
        <Select
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={TYPE_OPTIONS}
          fullWidth={false}
          className="w-40"
          aria-label={t('billing.chartOfAccounts.type')}
        />
        <Select
          value={subtype}
          onChange={(e) => setSubtype(e.target.value)}
          options={SUBTYPE_OPTIONS}
          fullWidth={false}
          className="w-52"
          aria-label={t('billing.chartOfAccounts.subtype')}
        />
        <Select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          options={CURRENCY_OPTIONS}
          fullWidth={false}
          className="w-32"
          aria-label={t('billing.chartOfAccounts.currency')}
        />
      </div>

      {moreOpen && (
        <div className="border-t border-border px-3.5 py-2">
          <FormRow label={t('billing.chartOfAccounts.parent')} labelWidth="sm" htmlFor="account-create-parent">
            <input
              id="account-create-parent"
              type="text"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              placeholder={t('billing.chartOfAccounts.parentPlaceholder')}
              autoCapitalize="off"
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}

export default AccountFormDialog;
